{/* Copyright 2026 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {Layout} from '../../../src/Layout';
export default Layout;
import {FunctionAPIGroup} from '../../../src/FunctionAPI';
import {InterfaceType} from '../../../src/types';
import docs from 'docs:@react-aria/radio';
import statelyDocs from 'docs:@react-stately/radio';
import Anatomy from '/packages/dev/s2-docs/pages/react-aria/RadioGroupAnatomy.svg';

export const section = 'Hooks';
export const description = 'Provides the behavior and accessibility implementation for a radio group component.';
export const isSubpage = true;

# useRadioGroup

<PageDescription>{docs.exports.useRadioGroup.description}</PageDescription>

```tsx render files={["starters/hooks/src/RadioGroup.tsx", "starters/hooks/src/RadioGroup.css"]}
"use client";
import {RadioGroup, Radio} from 'hooks-starter/RadioGroup';

<RadioGroup label="Favorite pet">
  <Radio value="dogs">Dogs</Radio>
  <Radio value="cats">Cats</Radio>
</RadioGroup>
```

## API

<Anatomy />

```tsx
<RadioGroup>
  <Label />
  <RadioField>
    <RadioButton>
      <SelectionIndicator />
    </RadioButton>
    <Text slot="description" />
  </RadioField>
  <Text slot="description" />
  <FieldError />
</RadioGroup>
```

<FunctionAPIGroup
  functions={[
    {function: statelyDocs.exports.useRadioGroupState, links: statelyDocs.links},
    {function: docs.exports.useRadioGroup, links: docs.links},
    {function: docs.exports.useRadio, links: docs.links},
  ]}
/>

### RadioGroupState

<InterfaceType {...statelyDocs.exports.RadioGroupState} links={statelyDocs.links} />

### AriaRadioGroupProps

<InterfaceType {...docs.exports.AriaRadioGroupProps} />

### RadioGroupAria

<InterfaceType {...docs.exports.RadioGroupAria} />

### AriaRadioProps

<InterfaceType {...docs.exports.AriaRadioProps} />

### RadioAria

<InterfaceType {...docs.exports.RadioAria} />
